import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useIdioma } from '../../i18n/idioma';
import {
  Play, Pause, X, Volume2, VolumeX, Maximize2, GripVertical, Video, Loader2,
  AlertTriangle, RotateCcw,
} from 'lucide-react';
import { useHelpVideo } from '../../contexts/HelpVideoContext';
import {
  fitToViewport, defaultGeom, parseGeom, clamp, MIN_W, MIN_H, DEFAULT_W, DEFAULT_H, EDGE,
  type Geom, type Viewport,
} from './boxGeometry';

// ─────────────────────────────────────────────────────────────────────────────
// HelpVideoBox — the ONE floating tutorial player, mounted at the app root.
//
// It lives outside the page routes and never unmounts, so the user can keep it
// open and keep working — start a job, pick a client, open the estimate
// builder — and it stays put: same position, same size, same playback spot.
//
// ── WHY THIS FILE LOOKS LIKE IT DOES ────────────────────────────────────────
//
// Drag, resize and the progress bar all write to the DOM DIRECTLY and never
// call setState. That is not premature optimisation, it is the whole point:
//
//   * `pointermove` fires once per frame (60-120Hz). Calling setState there
//     re-rendered this entire subtree — ~40 elements and 7 icons — on every
//     frame of every drag, on a phone, while a video was decoding beside it.
//   * The old code also animated `left`/`top`, which are LAYOUT properties, so
//     each frame forced a reflow. `transform` is composited: no layout, no
//     paint, handled off the main thread.
//   * `timeupdate` fires ~4x/second for the whole video. Re-rendering to move
//     a progress bar 380px wide by a fraction of a pixel is work for nothing.
//
// So: geometry lives in a ref and is applied with `style.transform`; the scrub
// bar and clock are written through refs. React state is reserved for things
// that genuinely change the UI shape — playing, muted, buffering, error.
//
// The net effect is that dragging or resizing this box costs ZERO React
// renders, and a playing video costs zero renders per second.
// ─────────────────────────────────────────────────────────────────────────────

/** Pixels of movement before a press counts as a drag. Below this it is a
 *  click, which is what stops "I grabbed the title bar" turning into
 *  "I pressed the close button". */
const DRAG_THRESHOLD = 4;

const STORE_KEY = 'razapro.ayuda.video.geom';

const formatTime = (s: number): string => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const viewport = (): Viewport => ({
  w: typeof window !== 'undefined' ? window.innerWidth : 1024,
  h: typeof window !== 'undefined' ? window.innerHeight : 768,
});

const fit = (g: Geom) => fitToViewport(g, viewport());
const initialGeom = () => defaultGeom(viewport());

function loadGeom(): Geom {
  let raw: string | null = null;
  try { raw = window.localStorage?.getItem(STORE_KEY) ?? null; } catch { /* private mode */ }
  const saved = parseGeom(raw);
  return saved ? fit(saved) : initialGeom();
}

const HelpVideoBox: React.FC = () => {
  const { t } = useIdioma();
  const { isOpen, topic, closeHelpVideo, getVideo, loading } = useHelpVideo();
  const video = topic ? getVideo(topic) : null;

  const boxRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scrubRef = useRef<HTMLInputElement>(null);
  const clockRef = useRef<HTMLSpanElement>(null);

  /** The single source of truth for where and how big the box is. Deliberately
   *  a ref: React never needs to know, because nothing re-renders when it
   *  changes — the DOM is written directly. */
  const geom = useRef<Geom>(loadGeom());
  const drag = useRef<{ px: number; py: number; ox: number; oy: number; moved: boolean } | null>(null);
  const resize = useRef<{ px: number; py: number; ow: number; oh: number } | null>(null);

  // Only genuine UI-shape changes live in state.
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [failed, setFailed] = useState(false);
  const [askClose, setAskClose] = useState(false);
  /** Set when play() is rejected — almost always an autoplay-policy block.
   *  Swallowing that rejection is why the old play button could look dead. */
  const [needsGesture, setNeedsGesture] = useState(false);

  const applyGeom = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const g = geom.current;
    // translate3d, not left/top: composited, so a drag never touches layout.
    el.style.transform = `translate3d(${g.x}px, ${g.y}px, 0)`;
    el.style.width = `${g.w}px`;
    el.style.height = `${g.h}px`;
  }, []);

  const persist = useCallback(() => {
    try { window.localStorage?.setItem(STORE_KEY, JSON.stringify(geom.current)); } catch { /* non-fatal */ }
  }, []);

  // ── Apply geometry whenever the box appears ──────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    geom.current = fit(geom.current);
    applyGeom();
  }, [isOpen, applyGeom]);

  // ── Keep it on screen when the viewport changes ──────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => { geom.current = fit(geom.current); applyGeom(); persist(); };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isOpen, applyGeom, persist]);

  // ── Reset transient playback UI when the loaded tutorial changes ─────────
  useEffect(() => {
    setPlaying(false);
    setDuration(0);
    setBuffering(false);
    setFailed(false);
    setNeedsGesture(false);
    setAskClose(false);
    if (scrubRef.current) scrubRef.current.value = '0';
    if (clockRef.current) clockRef.current.textContent = '0:00 / 0:00';
  }, [topic]);

  // ── Drag ─────────────────────────────────────────────────────────────────
  const onDragDown = useCallback((e: React.PointerEvent) => {
    // Capture on currentTarget, not target: capturing on whatever child was hit
    // (an icon inside the close button) is what let a drag end as a click on
    // the X and dismiss the box.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, ox: geom.current.x, oy: geom.current.y, moved: false };
  }, []);

  const onDragMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // A stale ref plus a hover would otherwise drag the box with no button held.
    if (e.buttons === 0) { drag.current = null; return; }

    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;

    const { w: vw, h: vh } = viewport();
    geom.current.x = clamp(d.ox + dx, EDGE, Math.max(EDGE, vw - geom.current.w - EDGE));
    geom.current.y = clamp(d.oy + dy, EDGE, Math.max(EDGE, vh - geom.current.h - EDGE));
    applyGeom();
  }, [applyGeom]);

  const endDrag = useCallback((e: React.PointerEvent) => {
    if (drag.current) persist();
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, [persist]);

  // ── Resize ───────────────────────────────────────────────────────────────
  const onResizeDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    resize.current = { px: e.clientX, py: e.clientY, ow: geom.current.w, oh: geom.current.h };
  }, []);

  const onResizeMove = useCallback((e: React.PointerEvent) => {
    const r = resize.current;
    if (!r) return;
    if (e.buttons === 0) { resize.current = null; return; }
    e.stopPropagation();

    const { w: vw, h: vh } = viewport();
    geom.current.w = clamp(r.ow + (e.clientX - r.px), MIN_W, vw - geom.current.x - EDGE);
    geom.current.h = clamp(r.oh + (e.clientY - r.py), MIN_H, vh - geom.current.y - EDGE);
    applyGeom();
  }, [applyGeom]);

  const endResize = useCallback((e: React.PointerEvent) => {
    if (resize.current) persist();
    resize.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, [persist]);

  const resetGeom = useCallback(() => {
    geom.current = initialGeom();
    applyGeom();
    persist();
  }, [applyGeom, persist]);

  // ── Playback ─────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || !video?.src) return;
    if (el.paused) {
      setNeedsGesture(false);
      el.play().catch(() => setNeedsGesture(true));
    } else {
      el.pause();
    }
  }, [video?.src]);

  const toggleMute = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  }, []);

  /** Written straight to the DOM. This runs ~4x/second for the length of the
   *  video; going through state would re-render the whole box each time. */
  const onTimeUpdate = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    const d = el.duration;
    if (scrubRef.current && isFinite(d) && d > 0 && document.activeElement !== scrubRef.current) {
      const pct = (el.currentTime / d) * 100;
      scrubRef.current.value = String(pct);
      scrubRef.current.style.background =
        `linear-gradient(to right, #FACC15 ${pct}%, #1A3A6B ${pct}%)`;
    }
    if (clockRef.current) {
      clockRef.current.textContent = `${formatTime(el.currentTime)} / ${formatTime(isFinite(d) ? d : 0)}`;
    }
  }, []);

  const onSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const el = videoRef.current;
    if (!el || !isFinite(el.duration) || el.duration <= 0) return;
    el.currentTime = (Number(e.target.value) / 100) * el.duration;
  }, []);

  const goFullscreen = useCallback(() => {
    const el = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!el) return;
    // iPhone Safari implements none of the Fullscreen API on elements — only
    // this WebKit-specific method — so without the fallback the button was
    // simply dead on the phones most of the crew use.
    if (el.requestFullscreen) el.requestFullscreen().catch(() => { /* non-fatal */ });
    else el.webkitEnterFullscreen?.();
  }, []);

  const retry = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    setFailed(false);
    setBuffering(true);
    el.load();
  }, []);

  /** Closing mid-video loses the viewer's place, so it asks first. If nothing
   *  has been watched there is nothing to lose and it just closes — a
   *  confirmation nobody needs is the fastest way to teach people to click
   *  through confirmations without reading them. */
  const requestClose = useCallback(() => {
    const el = videoRef.current;
    const watched = !!el && el.currentTime > 1 && !el.ended;
    if (watched) setAskClose(true);
    else closeHelpVideo();
  }, [closeHelpVideo]);

  if (!isOpen || !video) return null;

  return (
    <div
      ref={boxRef}
      className="fixed left-0 top-0 z-[245] rounded-2xl shadow-s3 bg-navy-850 overflow-hidden select-none flex flex-col"
      /* Geometry is applied imperatively in applyGeom(). Starting off-screen
         avoids a one-frame flash at 0,0 before the first effect runs. */
      style={{ transform: 'translate3d(-9999px, -9999px, 0)', width: DEFAULT_W, height: DEFAULT_H }}
      role="dialog"
      aria-label={video.title}
    >
      {/* ── Header / drag handle ── */}
      <div
        onPointerDown={onDragDown}
        onPointerMove={onDragMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={() => { drag.current = null; }}
        className="flex items-center gap-2 px-3 py-2 bg-navy-850 border-b border-navy-800 cursor-grab active:cursor-grabbing touch-none shrink-0"
      >
        <GripVertical size={16} className="text-acero-600 shrink-0" />
        <div className="w-6 h-6 rounded-lg bg-brand-400 flex items-center justify-center shrink-0">
          <Video size={13} className="text-navy-950" />
        </div>
        <h3 className="flex-1 text-white font-semibold text-sm tracking-tight min-w-0 truncate">{video.title}</h3>

        <button
          onClick={resetGeom}
          /* stopPropagation on pointerdown keeps the header's drag handler from
             claiming the press — otherwise the button never receives a click. */
          onPointerDown={e => e.stopPropagation()}
          className="p-1.5 text-acero-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          title={t('ayuda.video.restablecer')}
          aria-label={t('ayuda.video.restablecer')}
        >
          <RotateCcw size={14} />
        </button>
        <button
          onClick={requestClose}
          onPointerDown={e => e.stopPropagation()}
          className="p-1.5 text-acero-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          title={t('ayuda.video.cerrar_tooltip')}
          aria-label={t('ayuda.video.cerrar_aria')}
        >
          <X size={16} />
        </button>
      </div>

      {/* ── Video area — flexes so the box can be any size ── */}
      <div className="relative bg-navy-850 flex-1 min-h-0">
        {video.src ? (
          <>
            <video
              ref={videoRef}
              src={video.src}
              poster={video.poster}
              className="w-full h-full object-contain bg-navy-850"
              onClick={togglePlay}
              onPlay={() => { setPlaying(true); setNeedsGesture(false); }}
              onPause={() => setPlaying(false)}
              onTimeUpdate={onTimeUpdate}
              onLoadedMetadata={e => { setDuration(e.currentTarget.duration); onTimeUpdate(); }}
              onEnded={() => setPlaying(false)}
              /* Without these three a stall on bad site wifi is a frozen frame
                 with no explanation — the user assumes the app hung. */
              onWaiting={() => setBuffering(true)}
              onStalled={() => setBuffering(true)}
              onPlaying={() => { setBuffering(false); setFailed(false); }}
              onCanPlay={() => setBuffering(false)}
              onError={() => { setBuffering(false); setFailed(true); }}
              playsInline
              preload="metadata"
            />

            {failed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-navy-950/80">
                <AlertTriangle size={20} className="text-peligro" />
                <p className="text-white text-sm font-semibold mt-2">{t('ayuda.video.error_titulo')}</p>
                <button
                  onClick={retry}
                  className="mt-3 px-3 h-8 rounded-lg bg-brand-400 text-navy-950 text-xs font-semibold hover:bg-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  {t('ayuda.video.error_reintentar')}
                </button>
              </div>
            )}

            {!failed && buffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-navy-950/30 pointer-events-none">
                <Loader2 size={26} className="text-brand-400 animate-spin" />
              </div>
            )}

            {!failed && !buffering && !playing && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex flex-col items-center justify-center bg-navy-950/40 hover:bg-navy-950/25 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400"
                aria-label={t('ayuda.video.reproducir_aria')}
              >
                <span className="w-14 h-14 rounded-full bg-brand-400 text-navy-950 flex items-center justify-center shadow-s2">
                  <Play size={26} className="ml-0.5" fill="currentColor" />
                </span>
                {needsGesture && (
                  <span className="mt-2 text-[11px] text-white/80">{t('ayuda.video.toca_para_ver')}</span>
                )}
              </button>
            )}
          </>
        ) : loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Loader2 size={22} className="text-brand-400 animate-spin" />
            <p className="text-acero-400 text-xs mt-3">{t('ayuda.video.cargando')}</p>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="w-10 h-10 rounded-xl bg-white/10 grid place-items-center mb-3">
              <Video size={18} className="text-brand-400" />
            </div>
            <p className="text-white font-semibold tracking-tight text-sm">{t('ayuda.video.proximamente')}</p>
            <p className="text-acero-400 text-xs mt-1.5 leading-relaxed">{t('ayuda.video.sin_video_desc')}</p>
          </div>
        )}
      </div>

      {/* ── Close confirmation — inline, not a modal. A dialog over a 380px
             floating box would cover the thing it is asking about. ── */}
      {askClose && (
        <div className="px-3 py-2.5 bg-navy-900 border-t border-navy-800 flex items-center gap-2 shrink-0">
          <p className="flex-1 text-xs text-white leading-snug">{t('ayuda.video.cerrar_confirma')}</p>
          <button
            onClick={() => setAskClose(false)}
            className="h-8 px-2.5 rounded-lg text-xs font-medium text-acero-300 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {t('ayuda.video.cerrar_confirma_no')}
          </button>
          <button
            onClick={closeHelpVideo}
            className="h-8 px-3 rounded-lg bg-brand-400 text-navy-950 text-xs font-semibold hover:bg-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {t('ayuda.video.cerrar_confirma_si')}
          </button>
        </div>
      )}

      {/* ── Controls ── */}
      {video.src && !askClose && (
        <div className="px-3 py-2.5 bg-navy-850 border-t border-navy-800 flex items-center gap-2.5 shrink-0">
          <button
            onClick={togglePlay}
            className="w-9 h-9 rounded-full bg-brand-400 text-navy-950 hover:bg-brand-300 flex items-center justify-center transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-850"
            title={playing ? t('video.pausa') : t('video.reproducir')}
            aria-label={playing ? t('video.pausa') : t('video.reproducir')}
          >
            {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
          </button>

          <input
            ref={scrubRef}
            type="range"
            min={0}
            max={100}
            step={0.1}
            defaultValue={0}
            disabled={!duration}
            onChange={onSeek}
            /* El ÚNICO acento de la app que no es el del sistema, y a propósito.
               `accent-color: navy-850` se hereda de <html> (index.css), pero
               esta barra va sobre `bg-navy-800`: un pomo navy sobre pista navy
               no se vería. El amarillo es el mismo con el que se pinta la parte
               reproducida en el gradiente de abajo, así que la barra entera
               habla un solo color. Si lo quitas, desaparece el pomo. */
            className="flex-1 min-w-0 h-1.5 rounded-full appearance-none cursor-pointer accent-brand-400 bg-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            style={{ background: 'linear-gradient(to right, #FACC15 0%, #1A3A6B 0%)' }}
            aria-label={t('ayuda.video.barra_aria')}
          />

          <span ref={clockRef} className="font-data text-[11px] text-acero-300 tabular-nums shrink-0">0:00 / 0:00</span>

          <button
            onClick={toggleMute}
            className="p-1.5 text-acero-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            title={muted ? t('video.ponerSonido') : t('video.quitarSonido')}
            aria-label={muted ? t('video.ponerSonido') : t('video.quitarSonido')}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            onClick={goFullscreen}
            className="p-1.5 text-acero-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-150 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            title={t('ayuda.video.pantalla_completa_tooltip')}
            aria-label={t('ayuda.video.pantalla_completa_aria')}
          >
            <Maximize2 size={15} />
          </button>
        </div>
      )}

      {/* ── Resize handle ──
           Its own pointer capture, so resizing never reaches the drag handler.
           28px is a real touch target; the visual grip is smaller. */}
      <div
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        onLostPointerCapture={() => { resize.current = null; }}
        role="separator"
        aria-label={t('ayuda.video.redimensionar_aria')}
        className="absolute bottom-0 right-0 w-7 h-7 cursor-se-resize touch-none z-10 flex items-end justify-end p-1.5 group"
      >
        <span className="block w-2.5 h-2.5 border-r-2 border-b-2 border-acero-500 group-hover:border-brand-400 rounded-br-sm transition-colors" />
      </div>
    </div>
  );
};

export default HelpVideoBox;
